import { tool } from 'ai'
import { BACKEND_URL } from '../config'
import { parseAddressWithSheet } from '../excel-utils'
import { getAuthToken } from '../supabase'
import { fetchDataFromUrl, queryDatabase } from './common-tools'
import { getCsvAttachment, getCsvAttachmentCount } from './csv-store'
import { toolSchemas } from './tool-schemas'
import { columnNumberToLetter } from './tool-utils'

/**
 * Tool definitions for Excel operations using Vercel AI SDK 6
 * These tools enable the agent to read/write data, inspect sheet structure,
 * and manage formatting in the active Excel workbook.
 */

/**
 * Helper function to get a worksheet and range from an address.
 * Handles sheet name prefixes (e.g., "Sheet1!A1:B10") automatically.
 * Must be called within an Excel.run() context.
 */
function getSheetAndRange(
  context: Excel.RequestContext,
  address: string
): { sheet: Excel.Worksheet; range: Excel.Range } {
  const { sheetName, rangeAddress } = parseAddressWithSheet(address)
  const sheet = sheetName
    ? context.workbook.worksheets.getItem(sheetName)
    : context.workbook.worksheets.getActiveWorksheet()
  const range = sheet.getRange(rangeAddress)
  return { sheet, range }
}

export const readCellRanges = tool({
  ...toolSchemas.readCellRanges,
  execute: async ({ addresses }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        address: string
        values: unknown[][]
        rowCount: number
        columnCount: number
      }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const address of addresses) {
          try {
            const { range } = getSheetAndRange(context, address)
            range.load(['values', 'address'])
            await context.sync()
            const values = range.values as unknown[][]
            results.push({
              address,
              values,
              rowCount: values?.length ?? 0,
              columnCount: values?.[0]?.length ?? 0,
            })
          } catch (err) {
            errors.push({
              address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to read range(s): ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Writes values to a specified range
 * For formulas, use setFormulaRanges instead
 */
export const writeCellRanges = tool({
  ...toolSchemas.writeCellRanges,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        address: string
        rowsWritten: number
        columnsWritten: number
      }> = []
      const errors: Array<{ address: string; error: string }> = []

      // Helper to check if address is a single cell (no colon) or a range
      const isSingleCell = (addr: string) => !addr.includes(':')

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const { sheet } = getSheetAndRange(context, item.address)
            let writtenAddress: string = ''

            if (item.values && item.values.length > 0) {
              let dataRange: Excel.Range
              const { rangeAddress } = parseAddressWithSheet(item.address)
              if (isSingleCell(rangeAddress)) {
                // Single cell: resize to fit the values array
                dataRange = sheet
                  .getRange(rangeAddress)
                  .getResizedRange(
                    item.values.length - 1,
                    item.values[0]?.length ? item.values[0].length - 1 : 0
                  )
              } else {
                // Range already specified: use it directly
                dataRange = sheet.getRange(rangeAddress)
              }
              dataRange.values = item.values

              // Load address before reading it
              dataRange.load(['address'])
              await context.sync()

              if (item.autofit ?? true) {
                dataRange.format.autofitColumns()
                dataRange.format.autofitRows()
                await context.sync()
              }

              writtenAddress = dataRange.address
            }

            results.push({
              address: writtenAddress,
              rowsWritten: item.values?.length ?? 0,
              columnsWritten: item.values?.[0]?.length ?? 0,
            })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to write cell ranges: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Paste a pre-parsed CSV attachment directly into the sheet.
 * The CSV is parsed at send-time and stored in csv-store; this tool
 * just reads the parsed data and writes it via the Office.js API.
 */
export const pasteAttachedCsvToSheet = tool({
  ...toolSchemas.pasteAttachedCsvToSheet,
  execute: async ({ fileIndex = 0, startCell = 'A1', autofit = true }) => {
    try {
      const count = getCsvAttachmentCount()
      if (count === 0) {
        return {
          error: 'No CSV attachments found. The user must attach a CSV file.',
        }
      }
      const csv = getCsvAttachment(fileIndex)
      if (!csv) {
        return {
          error: `CSV attachment at index ${fileIndex} not found. ${count} CSV file(s) available (0-indexed).`,
        }
      }

      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let writtenAddress = ''
      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet } = getSheetAndRange(context, startCell)
        const { rangeAddress } = parseAddressWithSheet(startCell)

        const dataRange = sheet
          .getRange(rangeAddress)
          .getResizedRange(csv.rowCount - 1, csv.columnCount - 1)
        dataRange.values = csv.rows
        dataRange.load(['address'])
        await context.sync()

        if (autofit) {
          dataRange.format.autofitColumns()
          dataRange.format.autofitRows()
          await context.sync()
        }

        writtenAddress = dataRange.address
      })

      return {
        success: true,
        filename: csv.filename,
        address: writtenAddress,
        rowsWritten: csv.rowCount,
        columnsWritten: csv.columnCount,
      }
    } catch (err) {
      return {
        error: `Failed to paste CSV: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Adjusts cell references in a formula by row and column offsets
 * e.g., "=B2*C2" with rowOffset=1 becomes "=B3*C3"
 * Handles absolute references ($A$1, $A1, A$1) correctly
 */
function adjustFormulaReferences(
  formula: string,
  rowOffset: number,
  colOffset: number
): string {
  // Match cell references like A1, $A1, A$1, $A$1, AA123, etc.
  return formula.replace(
    /(\$?)([A-Z]+)(\$?)(\d+)/gi,
    (_match, colAbsolute, col, rowAbsolute, row) => {
      let newCol = col
      let newRow = row

      // Adjust column if not absolute
      if (colAbsolute !== '$' && colOffset !== 0) {
        // Convert column letters to number, add offset, convert back
        let colNum = 0
        for (let i = 0; i < col.length; i++) {
          colNum = colNum * 26 + (col.charCodeAt(i) - 64)
        }
        colNum += colOffset
        newCol = columnNumberToLetter(colNum)
      }

      // Adjust row if not absolute
      if (rowAbsolute !== '$' && rowOffset !== 0) {
        newRow = String(Number.parseInt(row, 10) + rowOffset)
      }

      return `${colAbsolute}${newCol}${rowAbsolute}${newRow}`
    }
  )
}

/**
 * Sets a formula on a range of cells, adjusting references for each cell
 * Write the formula for the top-left cell - references adjust automatically
 */
export const setFormulaRanges = tool({
  ...toolSchemas.setFormulaRanges,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        address: string
        formula: string
        rowCount: number
        columnCount: number
      }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            if (!item.formula.startsWith('=')) {
              errors.push({
                address: item.address,
                error: 'Formula must start with "="',
              })
              continue
            }

            const { range } = getSheetAndRange(context, item.address)
            range.load(['address', 'rowCount', 'columnCount'])
            await context.sync()

            const rowCount = range.rowCount
            const columnCount = range.columnCount

            // Build 2D formulas array with adjusted references for each cell
            const formulas: string[][] = []
            for (let r = 0; r < rowCount; r++) {
              const row: string[] = []
              for (let c = 0; c < columnCount; c++) {
                row.push(adjustFormulaReferences(item.formula, r, c))
              }
              formulas.push(row)
            }

            range.formulas = formulas
            await context.sync()

            if (item.autofit ?? true) {
              range.format.autofitColumns()
              await context.sync()
            }

            results.push({
              address: range.address,
              formula: item.formula,
              rowCount,
              columnCount,
            })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to set formula ranges: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Gets information about the active worksheet
 * Useful for understanding sheet structure and finding where to place data
 */
export const readSheetContext = tool({
  ...toolSchemas.readSheetContext,
  execute: async ({ detailed }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let sheetInfo: {
        sheetName: string
        usedRange: string
        rowCount: number
        columnCount: number
        values?: unknown[][]
      } = {
        sheetName: '',
        usedRange: '',
        rowCount: 0,
        columnCount: 0,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        sheet.load(['name'])
        const used = sheet.getUsedRange(true)

        // Only load values if detailed is true to avoid loading large datasets
        if (detailed) {
          used.load(['address', 'values', 'rowCount', 'columnCount'])
        } else {
          used.load(['address', 'rowCount', 'columnCount'])
        }
        await context.sync()

        sheetInfo = {
          sheetName: sheet.name,
          usedRange: used.address,
          rowCount: used.rowCount,
          columnCount: used.columnCount,
        }

        if (detailed) {
          sheetInfo.values = used.values
        }
      })

      return {
        success: true,
        ...sheetInfo,
      }
    } catch (err) {
      return {
        error: `Failed to read sheet context: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getCellFormat = tool({
  ...toolSchemas.getCellFormat,
  execute: async ({ addresses }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        address: string
        format: {
          backgroundColor?: string
          fontBold?: boolean
          fontItalic?: boolean
          fontColor?: string
          fontSize?: number
          numberFormat?: string[][]
          horizontalAlignment?: string
          verticalAlignment?: string
          wrapText?: boolean
          rowHeight?: number
          columnWidth?: number
        }
      }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const address of addresses) {
          try {
            const { range } = getSheetAndRange(context, address)

            // Load format properties
            range.load(['numberFormat'])
            range.format.load([
              'fill',
              'font',
              'horizontalAlignment',
              'verticalAlignment',
              'wrapText',
              'rowHeight',
              'columnWidth',
            ])
            range.format.fill.load(['color'])
            range.format.font.load(['bold', 'italic', 'color', 'size'])

            await context.sync()

            const format: {
              backgroundColor?: string
              fontBold?: boolean
              fontItalic?: boolean
              fontColor?: string
              fontSize?: number
              numberFormat?: string[][]
              horizontalAlignment?: string
              verticalAlignment?: string
              wrapText?: boolean
              rowHeight?: number
              columnWidth?: number
            } = {}

            // Background color
            if (range.format.fill.color) {
              format.backgroundColor = range.format.fill.color
            }

            // Font properties
            format.fontBold = range.format.font.bold
            format.fontItalic = range.format.font.italic
            if (range.format.font.color) {
              format.fontColor = range.format.font.color
            }
            format.fontSize = range.format.font.size

            // Number format
            if (range.numberFormat && range.numberFormat.length > 0) {
              format.numberFormat = range.numberFormat
            }

            // Alignment
            format.horizontalAlignment = range.format.horizontalAlignment
            format.verticalAlignment = range.format.verticalAlignment
            format.wrapText = range.format.wrapText

            // Dimensions
            format.rowHeight = range.format.rowHeight
            format.columnWidth = range.format.columnWidth

            results.push({ address, format })
          } catch (err) {
            errors.push({
              address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to get cell format: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const formatCells = tool({
  ...toolSchemas.formatCells,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{ address: string; formattingApplied: string[] }> =
        []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const { range } = getSheetAndRange(context, item.address)
            const formatting = item.formatting

            const formattingApplied = Object.keys(formatting).filter(
              (key) => formatting[key as keyof typeof formatting] !== undefined
            )

            if (formatting.backgroundColor) {
              range.format.fill.color = formatting.backgroundColor
            }

            if (formatting.fontBold !== undefined) {
              range.format.font.bold = formatting.fontBold
            }

            if (formatting.fontItalic !== undefined) {
              range.format.font.italic = formatting.fontItalic
            }

            if (formatting.fontColor) {
              range.format.font.color = formatting.fontColor
            }

            if (formatting.fontSize) {
              range.format.font.size = formatting.fontSize
            }

            if (formatting.numberFormat) {
              // numberFormat is a 2D array matching the range dimensions
              const numFormat = formatting.numberFormat
              const numFormatArray: string[][] = []
              range.load(['rowCount', 'columnCount'])
              await context.sync()
              for (let r = 0; r < range.rowCount; r++) {
                const row: string[] = []
                for (let c = 0; c < range.columnCount; c++) {
                  row.push(
                    typeof numFormat === 'string'
                      ? numFormat
                      : String(numFormat)
                  )
                }
                numFormatArray.push(row)
              }
              range.numberFormat = numFormatArray
            }

            if (formatting.horizontalAlignment) {
              range.format.horizontalAlignment = formatting.horizontalAlignment
            }

            if (formatting.verticalAlignment) {
              range.format.verticalAlignment = formatting.verticalAlignment
            }

            if (formatting.wrapText !== undefined) {
              range.format.wrapText = formatting.wrapText
            }

            await context.sync()
            results.push({ address: item.address, formattingApplied })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to format cells: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Gets dimensions of the active worksheet
 * Useful for validating that data fits or finding insertion points
 */
export const getSheetDimensions = tool({
  ...toolSchemas.getSheetDimensions,
  execute: async ({ includeUsedRange }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let dimensions: {
        sheetName: string
        maxRows?: number
        maxColumns?: number
        usedRange?: string
        usedRows?: number
        usedColumns?: number
      } = {
        sheetName: '',
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        sheet.load(['name'])

        if (includeUsedRange) {
          const used = sheet.getUsedRange(true)
          used.load(['address', 'rowCount', 'columnCount'])
          await context.sync()
          dimensions = {
            sheetName: sheet.name,
            usedRange: used.address,
            usedRows: used.rowCount,
            usedColumns: used.columnCount,
          }
        } else {
          await context.sync()
          dimensions = {
            sheetName: sheet.name,
            maxRows: 1048576, // Excel max rows
            maxColumns: 16384, // Excel max columns
          }
        }
      })

      return {
        success: true,
        ...dimensions,
      }
    } catch (err) {
      return {
        error: `Failed to get sheet dimensions: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Gets the currently selected range in the active worksheet
 * Useful for determining where the user wants to insert data or what they're working with
 */
export const getSelectedRange = tool({
  ...toolSchemas.getSelectedRange,
  execute: async ({ includeValues }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let selectionInfo: {
        address: string | null
        rowCount: number
        columnCount: number
        values?: unknown[][]
      } = {
        address: null,
        rowCount: 0,
        columnCount: 0,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const selection = context.workbook.getSelectedRange()
        selection.load(['address', 'rowCount', 'columnCount'])

        if (includeValues) {
          selection.load(['values'])
        }

        await context.sync()

        selectionInfo = {
          address: selection.address,
          rowCount: selection.rowCount,
          columnCount: selection.columnCount,
        }

        if (includeValues) {
          selectionInfo.values = selection.values
        }
      })

      return {
        success: true,
        ...selectionInfo,
      }
    } catch (err) {
      return {
        error: `Failed to get selected range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Executes JavaScript code in the Excel context
 * Useful for complex operations that require custom logic or multiple Excel API calls
 */
export const executeCode = tool({
  ...toolSchemas.executeCodeExcel,
  execute: async ({ code }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      // Create an async function that executes the code with Excel API available
      // Handle both expressions and statements
      const wrappedCode =
        code.trim().endsWith(';') || code.trim().endsWith('}')
          ? code
          : `return ${code}`

      // Wrap in async IIFE to support await in user code
      const executeFunction = new Function(
        'Excel',
        `
        return (async () => {
          try {
            ${wrappedCode}
          } catch (err) {
            return { error: err.message, stack: err.stack };
          }
        })();
      `
      )

      const result = executeFunction(Excel)

      // If result is a Promise (e.g., from Excel.run), await it
      if (result && typeof result.then === 'function') {
        const awaitedResult = await result
        return {
          success: true,
          result: awaitedResult,
        }
      } else if (result && typeof result === 'object' && 'error' in result) {
        return {
          error: `Code execution error: ${result.error}`,
          stack: result.stack,
        }
      } else {
        return {
          success: true,
          result,
        }
      }
    } catch (err) {
      return {
        error: `Failed to execute code: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const clearCellRange = tool({
  ...toolSchemas.clearCellRange,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{ address: string; cleared: boolean }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            // Parse address to handle sheet name prefix (e.g., "Sheet1!A1:B10")
            const { sheetName, rangeAddress } = parseAddressWithSheet(
              item.address
            )

            // Get the correct worksheet
            const sheet = sheetName
              ? context.workbook.worksheets.getItem(sheetName)
              : context.workbook.worksheets.getActiveWorksheet()
            const range = sheet.getRange(rangeAddress)
            range.clear(item.clearFormats ? 'All' : 'Contents')
            await context.sync()
            results.push({ address: item.address, cleared: true })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to clear range(s): ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Inserts rows at a specified position
 */
export const insertRows = tool({
  ...toolSchemas.insertRows,
  execute: async ({ startRow, count, shiftDown }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        // Use A1 notation for row range: "A5:A7" for rows 5-7
        // For rows, always shift down (the only option for row insertion)
        const range = sheet.getRange(`A${startRow}:A${startRow + count - 1}`)
        range.insert('Down')
        await context.sync()
      })

      return {
        success: true,
        rowsInserted: count,
        startRow,
      }
    } catch (err) {
      return {
        error: `Failed to insert rows: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Inserts columns at a specified position
 */
export const insertColumns = tool({
  ...toolSchemas.insertColumns,
  execute: async ({ startColumn, count, shiftRight }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        // Convert column number to letter (1 = A, 2 = B, etc.)
        const colLetter = columnNumberToLetter(startColumn)
        const endColLetter = columnNumberToLetter(startColumn + count - 1)
        // Use A1 notation for column range: "C1:C1" for column C
        // For columns, always shift right (the only option for column insertion)
        const range = sheet.getRange(`${colLetter}1:${endColLetter}1`)
        range.insert('Right')
        await context.sync()
      })

      return {
        success: true,
        columnsInserted: count,
        startColumn,
      }
    } catch (err) {
      return {
        error: `Failed to insert columns: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Deletes rows from the worksheet
 */
export const deleteRows = tool({
  ...toolSchemas.deleteRows,
  execute: async ({ startRow, count, shiftUp }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        // Use A1 notation for row range: "A5:A7" for rows 5-7
        // For rows, always shift up (the only option for row deletion)
        const range = sheet.getRange(`A${startRow}:A${startRow + count - 1}`)
        range.delete('Up')
        await context.sync()
      })

      return {
        success: true,
        rowsDeleted: count,
        startRow,
      }
    } catch (err) {
      return {
        error: `Failed to delete rows: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Deletes columns from the worksheet
 */
export const deleteColumns = tool({
  ...toolSchemas.deleteColumns,
  execute: async ({ startColumn, count, shiftLeft }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        const colLetter = columnNumberToLetter(startColumn)
        const endColLetter = columnNumberToLetter(startColumn + count - 1)
        // Use A1 notation for column range: "C1:C1" for column C
        // For columns, always shift left (the only option for column deletion)
        const range = sheet.getRange(`${colLetter}1:${endColLetter}1`)
        range.delete('Left')
        await context.sync()
      })

      return {
        success: true,
        columnsDeleted: count,
        startColumn,
      }
    } catch (err) {
      return {
        error: `Failed to delete columns: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Converts a range to an Excel table
 */
export const createTable = tool({
  ...toolSchemas.createTable,
  execute: async ({ address, hasHeaders, tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let createdTableName: string = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet, range } = getSheetAndRange(context, address)
        const table = sheet.tables.add(range, hasHeaders)
        if (tableName) {
          table.name = tableName
        }
        table.load(['name'])
        await context.sync()
        createdTableName = table.name
      })

      return {
        success: true,
        address,
        tableName: createdTableName,
      }
    } catch (err) {
      return {
        error: `Failed to create table: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Sorts data in a range
 */
export const sortRange = tool({
  ...toolSchemas.sortRange,
  execute: async ({ address, key, ascending, hasHeaders }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        const sortFields = [
          {
            key: key - 1, // Convert to 0-based
            sortOn: 'Value' as const,
            ascending,
          },
        ]
        range.sort.apply(sortFields, hasHeaders, false)
        await context.sync()
      })

      return {
        success: true,
        address,
        sortedByColumn: key,
        ascending,
      }
    } catch (err) {
      return {
        error: `Failed to sort range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Finds and replaces text in a range
 */
export const findAndReplace = tool({
  ...toolSchemas.findAndReplace,
  execute: async ({
    address,
    findText,
    replaceText,
    matchCase,
    matchEntireCell,
  }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let replaceCount = 0

      await Excel.run(async (context: Excel.RequestContext) => {
        let searchRange: Excel.Range

        if (address) {
          searchRange = getSheetAndRange(context, address).range
        } else {
          const sheet = context.workbook.worksheets.getActiveWorksheet()
          const usedRange = sheet.getUsedRangeOrNullObject()
          usedRange.load(['address'])
          await context.sync()
          if (usedRange.address) {
            searchRange = usedRange
          } else {
            return { error: 'No data in sheet to search' }
          }
        }

        let found = searchRange.find(findText, {
          completeMatch: matchEntireCell,
          matchCase,
        })
        found.load(['address'])
        await context.sync()

        let lastAddress = ''
        // Replace all occurrences
        while (found.address && found.address !== lastAddress) {
          found.values = [[replaceText]]
          replaceCount++
          lastAddress = found.address

          // Find next occurrence
          const nextFound = searchRange.find(findText, {
            completeMatch: matchEntireCell,
            matchCase,
          })
          nextFound.load(['address'])
          await context.sync()

          if (!nextFound.address || nextFound.address === lastAddress) break
          found = nextFound
        }

        await context.sync()
      })

      return {
        success: true,
        findText,
        replaceText,
        replacements: replaceCount,
      }
    } catch (err) {
      return {
        error: `Failed to find and replace: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Merges cells in a range
 */
export const mergeCells = tool({
  ...toolSchemas.mergeCells,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{ address: string; merged: boolean }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const { range } = getSheetAndRange(context, item.address)
            range.merge(item.across ?? false)
            await context.sync()
            results.push({ address: item.address, merged: true })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to merge cells: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Unmerges cells in a range
 */
export const unmergeCells = tool({
  ...toolSchemas.unmergeCells,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{ address: string; unmerged: boolean }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const { range } = getSheetAndRange(context, item.address)
            range.unmerge()
            await context.sync()
            results.push({ address: item.address, unmerged: true })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to unmerge cells: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Adds a hyperlink to a cell
 */
export const addHyperlink = tool({
  ...toolSchemas.addHyperlink,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        address: string
        url: string
        displayText: string
      }> = []
      const errors: Array<{ address: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const { range } = getSheetAndRange(context, item.address)
            // Set the display text first
            range.values = [[item.displayText || item.url]]
            // Then set the hyperlink
            range.hyperlink = {
              address: item.url,
              textToDisplay: item.displayText || item.url,
            }
            await context.sync()

            results.push({
              address: item.address,
              url: item.url,
              displayText: item.displayText || item.url,
            })
          } catch (err) {
            errors.push({
              address: item.address,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to add hyperlink: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Lists all worksheets in the workbook
 */
export const listWorksheets = tool({
  ...toolSchemas.listWorksheets,
  execute: async () => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let worksheets: Array<{ name: string; isActive: boolean }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheets = context.workbook.worksheets
        // Load all items with their name property
        sheets.load('items')
        await context.sync()

        // Now load name for each item
        for (const sheet of sheets.items) {
          sheet.load('name')
        }

        const activeSheet = context.workbook.worksheets.getActiveWorksheet()
        activeSheet.load('name')
        await context.sync()

        const activeSheetName = activeSheet.name
        worksheets = sheets.items.map((sheet) => ({
          name: sheet.name,
          isActive: sheet.name === activeSheetName,
        }))
      })

      return {
        success: true,
        worksheets,
        count: worksheets.length,
      }
    } catch (err) {
      return {
        error: `Failed to list worksheets: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Switches to a different worksheet
 */
export const switchWorksheet = tool({
  ...toolSchemas.switchWorksheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getItem(sheetName)
        sheet.activate()
        await context.sync()
      })

      return {
        success: true,
        sheetName,
        activated: true,
      }
    } catch (err) {
      return {
        error: `Failed to switch worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Creates a new worksheet
 */
export const createWorksheet = tool({
  ...toolSchemas.createWorksheet,
  execute: async ({ items }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const results: Array<{
        sheetName: string
        created: boolean
      }> = []
      const errors: Array<{ sheetName: string; error: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const item of items) {
          try {
            const sheets = context.workbook.worksheets
            const newSheet = sheets.add(item.sheetName)
            if (item.position !== undefined) {
              newSheet.position = item.position
            }
            await context.sync()
            results.push({
              sheetName: item.sheetName,
              created: true,
            })
          } catch (err) {
            errors.push({
              sheetName: item.sheetName,
              error: err instanceof Error ? err.message : String(err),
            })
          }
        }

        // Activate the last created sheet
        if (results.length > 0) {
          const lastSheetName = results[results.length - 1].sheetName
          const lastSheet = context.workbook.worksheets.getItem(lastSheetName)
          lastSheet.activate()
          await context.sync()
        }
      })

      return {
        success: true,
        results,
        ...(errors.length > 0 && { errors }),
      }
    } catch (err) {
      return {
        error: `Failed to create worksheet(s): ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Copies a range to another location
 */
export const copyRange = tool({
  ...toolSchemas.copyRange,
  execute: async ({ sourceAddress, targetAddress, copyFormats }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range: sourceRange } = getSheetAndRange(context, sourceAddress)
        const { range: targetRange } = getSheetAndRange(context, targetAddress)

        // Load source and target dimensions
        sourceRange.load(['values', 'formulas', 'rowCount', 'columnCount'])
        targetRange.load(['rowCount', 'columnCount'])
        await context.sync()

        // Resize target to match source (getResizedRange adds rows/columns)
        const resizedTarget = targetRange.getResizedRange(
          sourceRange.rowCount - targetRange.rowCount,
          sourceRange.columnCount - targetRange.columnCount
        )

        // Copy values and formulas
        resizedTarget.values = sourceRange.values
        resizedTarget.formulas = sourceRange.formulas

        if (copyFormats) {
          sourceRange.load(['numberFormat'])
          sourceRange.format.load([
            'fill',
            'font',
            'horizontalAlignment',
            'verticalAlignment',
            'wrapText',
          ])
          await context.sync()

          resizedTarget.numberFormat = sourceRange.numberFormat
          resizedTarget.format.fill.color = sourceRange.format.fill.color
          resizedTarget.format.font.bold = sourceRange.format.font.bold
          resizedTarget.format.font.italic = sourceRange.format.font.italic
          resizedTarget.format.font.color = sourceRange.format.font.color
          resizedTarget.format.font.size = sourceRange.format.font.size
          resizedTarget.format.horizontalAlignment =
            sourceRange.format.horizontalAlignment
          resizedTarget.format.verticalAlignment =
            sourceRange.format.verticalAlignment
          resizedTarget.format.wrapText = sourceRange.format.wrapText
        }

        await context.sync()
      })

      return {
        success: true,
        sourceAddress,
        targetAddress,
        copied: true,
      }
    } catch (err) {
      return {
        error: `Failed to copy range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Creates a chart from a data range
 * Supports various chart types including column, line, pie, bar, and area charts
 */
export const createChart = tool({
  ...toolSchemas.createChart,
  execute: async ({
    dataRange,
    chartType,
    hasHeaders,
    title,
    position,
    width,
    height,
  }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      // Map chart type string to Excel.ChartType enum value
      const chartTypeMap: Record<string, Excel.ChartType> = {
        ColumnClustered: Excel.ChartType.columnClustered,
        ColumnStacked: Excel.ChartType.columnStacked,
        ColumnStacked100: Excel.ChartType.columnStacked100,
        Line: Excel.ChartType.line,
        LineStacked: Excel.ChartType.lineStacked,
        LineStacked100: Excel.ChartType.lineStacked100,
        Pie: Excel.ChartType.pie,
        BarClustered: Excel.ChartType.barClustered,
        BarStacked: Excel.ChartType.barStacked,
        BarStacked100: Excel.ChartType.barStacked100,
        Area: Excel.ChartType.area,
        AreaStacked: Excel.ChartType.areaStacked,
        AreaStacked100: Excel.ChartType.areaStacked100,
        Scatter: Excel.ChartType.xyscatter,
        Doughnut: Excel.ChartType.doughnut,
      }

      const excelChartType = chartTypeMap[chartType]
      if (!excelChartType) {
        return { error: `Invalid chart type: ${chartType}` }
      }

      let chartName: string = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet, range } = getSheetAndRange(context, dataRange)

        // Create chart - seriesBy determines how data is organized
        // 'Auto' lets Excel determine automatically, 'Rows' means series are in rows, 'Columns' means series are in columns
        const chart = sheet.charts.add(
          excelChartType,
          range,
          hasHeaders ? 'Auto' : 'Auto'
        )

        // Set chart title if provided
        if (title) {
          chart.title.text = title
          chart.title.format.font.bold = true
          chart.title.format.font.size = 14
        }

        // Position the chart
        if (position) {
          const { range: positionRange } = getSheetAndRange(context, position)
          positionRange.load(['top', 'left'])
          await context.sync()
          chart.left = positionRange.left
          chart.top = positionRange.top
        } else {
          // Position below the data range
          range.load(['address', 'rowCount'])
          await context.sync()
          const dataEndRow = range.rowCount
          const chartStartRow = dataEndRow + 2 // 2 rows below data
          const chartPositionRange = sheet.getRange(`A${chartStartRow}`)
          chartPositionRange.load(['top', 'left'])
          await context.sync()
          chart.left = chartPositionRange.left
          chart.top = chartPositionRange.top
        }

        // Set chart size
        if (width) {
          chart.width = width
        } else {
          chart.width = 400 // Default width
        }

        if (height) {
          chart.height = height
        } else {
          chart.height = 300 // Default height
        }

        chart.load(['name'])
        await context.sync()
        chartName = chart.name
      })

      return {
        success: true,
        chartType,
        dataRange,
        chartName,
        title: title || undefined,
      }
    } catch (err) {
      return {
        error: `Failed to create chart: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Sets data validation rules on a range (Excel only)
 */
export const setDataValidation = tool({
  ...toolSchemas.setDataValidation,
  execute: async ({ address, rule }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        const validation = range.dataValidation

        // Set validation rule based on type
        const operatorMap: Record<string, Excel.DataValidationOperator> = {
          Between: Excel.DataValidationOperator.between,
          NotBetween: Excel.DataValidationOperator.notBetween,
          Equal: Excel.DataValidationOperator.equalTo,
          NotEqual: Excel.DataValidationOperator.notEqualTo,
          GreaterThan: Excel.DataValidationOperator.greaterThan,
          LessThan: Excel.DataValidationOperator.lessThan,
          GreaterThanOrEqual: Excel.DataValidationOperator.greaterThanOrEqualTo,
          LessThanOrEqual: Excel.DataValidationOperator.lessThanOrEqualTo,
        }

        const operator = rule.operator
          ? operatorMap[rule.operator]
          : Excel.DataValidationOperator.between

        if (rule.type === 'List') {
          validation.rule = {
            list: {
              inCellDropDown: true,
              source: rule.formula1 || '',
            },
          }
        } else if (rule.type === 'Date') {
          validation.rule = {
            date: {
              formula1: rule.formula1 || '',
              formula2: rule.formula2,
              operator,
            },
          }
        } else if (rule.type === 'Time') {
          validation.rule = {
            time: {
              formula1: rule.formula1 || '',
              formula2: rule.formula2,
              operator,
            },
          }
        } else if (rule.type === 'WholeNumber') {
          validation.rule = {
            wholeNumber: {
              formula1: rule.formula1 || '',
              formula2: rule.formula2,
              operator,
            },
          }
        } else if (rule.type === 'Decimal') {
          validation.rule = {
            decimal: {
              formula1: rule.formula1 || '',
              formula2: rule.formula2,
              operator,
            },
          }
        } else if (rule.type === 'TextLength') {
          validation.rule = {
            textLength: {
              formula1: rule.formula1 || '',
              formula2: rule.formula2,
              operator,
            },
          }
        } else if (rule.type === 'Custom') {
          validation.rule = {
            custom: {
              formula: rule.formula1 || '',
            },
          }
        }

        // Set input message
        if (rule.showInputMessage) {
          validation.prompt = {
            showPrompt: true,
            title: rule.inputTitle || '',
            message: rule.inputMessage || '',
          }
        }

        // Set error alert
        if (rule.showErrorAlert) {
          const errorStyleMap: Record<string, Excel.DataValidationAlertStyle> =
            {
              Stop: Excel.DataValidationAlertStyle.stop,
              Warning: Excel.DataValidationAlertStyle.warning,
              Information: Excel.DataValidationAlertStyle.information,
            }
          validation.errorAlert = {
            showAlert: true,
            style: errorStyleMap[rule.errorStyle || 'Stop'],
            title: rule.errorTitle || '',
            message: rule.errorMessage || '',
          }
        }

        await context.sync()
      })

      return {
        success: true,
        address,
        validationType: rule.type,
      }
    } catch (err) {
      return {
        error: `Failed to set data validation: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Gets comments from a range (Excel only)
 */
export const getComments = tool({
  ...toolSchemas.getComments,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      // Handle address: undefined or empty array means get all comments
      const addresses = address && address.length > 0 ? address : undefined

      const allComments: Array<{
        address: string
        content: string
        author: string
      }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet()
        const commentCollection = sheet.comments
        commentCollection.load('items')
        await context.sync()

        for (const comment of commentCollection.items) {
          const commentLocation = comment.getLocation()
          commentLocation.load(['address'])
          comment.load(['content', 'authorEmail'])
          await context.sync()

          const cellAddress = commentLocation.address

          // If no addresses specified, get all comments
          if (!addresses) {
            allComments.push({
              address: cellAddress,
              content: comment.content,
              author: comment.authorEmail || 'Unknown',
            })
          } else {
            // Check if this comment's cell is in any of the specified ranges
            for (const addr of addresses) {
              const { rangeAddress } = parseAddressWithSheet(addr)
              if (cellAddress.includes(rangeAddress)) {
                allComments.push({
                  address: cellAddress,
                  content: comment.content,
                  author: comment.authorEmail || 'Unknown',
                })
                break // Only add once even if it matches multiple ranges
              }
            }
          }
        }
      })

      // Group comments by address range
      const results: Array<{
        address: string
        comments: Array<{ address: string; content: string; author: string }>
        count: number
      }> = []

      if (addresses) {
        for (const addr of addresses) {
          const { rangeAddress } = parseAddressWithSheet(addr)
          const rangeComments = allComments.filter((c) =>
            c.address.includes(rangeAddress)
          )
          results.push({
            address: addr,
            comments: rangeComments,
            count: rangeComments.length,
          })
        }
      } else {
        // No address specified - return all comments as single result
        results.push({
          address: '',
          comments: allComments,
          count: allComments.length,
        })
      }

      return {
        success: true,
        results,
        totalCount: allComments.length,
      }
    } catch (err) {
      return {
        error: `Failed to get comments: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Freezes panes at a specified position (Excel only)
 */
export const freezePanes = tool({
  ...toolSchemas.freezePanes,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet } = getSheetAndRange(context, address)
        const { rangeAddress } = parseAddressWithSheet(address)

        // Parse the cell address to get row and column
        const match = rangeAddress.match(/^([A-Z]+)(\d+)$/)
        if (!match) {
          throw new Error(`Invalid cell address: ${rangeAddress}`)
        }

        // Freeze panes: freezeAt takes a Range or string
        // If address is A1, unfreeze by passing null
        if (rangeAddress === 'A1') {
          sheet.freezePanes.freezeAt(null as unknown as Excel.Range)
        } else {
          // Freeze at the specified cell
          const freezeRange = sheet.getRange(rangeAddress)
          sheet.freezePanes.freezeAt(freezeRange)
        }

        await context.sync()
      })

      return {
        success: true,
        address,
        frozen: address !== 'A1',
      }
    } catch (err) {
      return {
        error: `Failed to freeze panes: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Gets rows from an Excel table (Excel only)
 */
export const getTableRows = tool({
  ...toolSchemas.getTableRows,
  execute: async ({ tableName, includeHeaders }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const rows: unknown[][] = []
      let headers: string[] = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        table.load(['name', 'worksheet'])
        const rowsCollection = table.rows
        rowsCollection.load('items')
        await context.sync()

        // Get headers
        const headerRow = table.getHeaderRowRange()
        headerRow.load(['values'])
        await context.sync()
        headers = (headerRow.values[0] as string[]) || []

        // Get data rows
        const startIndex = includeHeaders ? 0 : 1
        for (let i = startIndex; i < rowsCollection.items.length; i++) {
          const row = rowsCollection.items[i]
          row.load(['values'])
          await context.sync()
          rows.push(row.values[0] as unknown[])
        }
      })

      return {
        success: true,
        tableName,
        headers: includeHeaders ? headers : undefined,
        rows,
        rowCount: rows.length,
      }
    } catch (err) {
      return {
        error: `Failed to get table rows: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Adds a row to an Excel table (Excel only)
 */
export const addTableRow = tool({
  ...toolSchemas.addTableRow,
  execute: async ({ tableName, values }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        table.rows.add(null, [values])
        await context.sync()
      })

      return {
        success: true,
        tableName,
        values,
      }
    } catch (err) {
      return {
        error: `Failed to add table row: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Auto-fills a range with series, dates, or trends based on a source pattern
 */
export const autoFill = tool({
  ...toolSchemas.autoFill,
  execute: async ({ sourceAddress, destinationAddress, autoFillType }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const fillTypeMap: Record<string, Excel.AutoFillType> = {
        FillDefault: Excel.AutoFillType.fillDefault,
        FillCopy: Excel.AutoFillType.fillCopy,
        FillSeries: Excel.AutoFillType.fillSeries,
        FillFormats: Excel.AutoFillType.fillFormats,
        FillValues: Excel.AutoFillType.fillValues,
        FillDays: Excel.AutoFillType.fillDays,
        FillWeekdays: Excel.AutoFillType.fillWeekdays,
        FillMonths: Excel.AutoFillType.fillMonths,
        FillYears: Excel.AutoFillType.fillYears,
        LinearTrend: Excel.AutoFillType.linearTrend,
        GrowthTrend: Excel.AutoFillType.growthTrend,
        FlashFill: Excel.AutoFillType.flashFill,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range: sourceRange } = getSheetAndRange(context, sourceAddress)
        const { range: destinationRange } = getSheetAndRange(
          context,
          destinationAddress
        )

        const fillType = autoFillType
          ? fillTypeMap[autoFillType]
          : Excel.AutoFillType.fillDefault

        sourceRange.autoFill(destinationRange, fillType)
        await context.sync()
      })

      return {
        success: true,
        sourceAddress,
        destinationAddress,
        autoFillType: autoFillType || 'FillDefault',
      }
    } catch (err) {
      return {
        error: `Failed to auto-fill range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Removes duplicate rows from a range based on specified columns
 */
export const removeDuplicates = tool({
  ...toolSchemas.removeDuplicates,
  execute: async ({ address, columns, hasHeaders }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let result: {
        uniqueRemaining: number
        removed: number
      } = {
        uniqueRemaining: 0,
        removed: 0,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)

        // Convert 1-based column indices to 0-based
        const columnIndices = columns.map((col) => col - 1)

        const removeResult = range.removeDuplicates(
          columnIndices,
          hasHeaders ?? true
        )
        removeResult.load(['uniqueRemaining', 'removed'])
        await context.sync()

        result = {
          uniqueRemaining: removeResult.uniqueRemaining,
          removed: removeResult.removed,
        }
      })

      return {
        success: true,
        address,
        ...result,
      }
    } catch (err) {
      return {
        error: `Failed to remove duplicates: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Deletes a worksheet from the workbook
 */
export const deleteWorksheet = tool({
  ...toolSchemas.deleteWorksheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getItem(sheetName)
        sheet.delete()
        await context.sync()
      })

      return {
        success: true,
        sheetName,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Renames a worksheet
 */
export const renameWorksheet = tool({
  ...toolSchemas.renameWorksheet,
  execute: async ({ oldName, newName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getItem(oldName)
        sheet.name = newName
        await context.sync()
      })

      return {
        success: true,
        oldName,
        newName,
      }
    } catch (err) {
      return {
        error: `Failed to rename worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Finds cells matching specific criteria (blanks, formulas, errors, etc.)
 */
export const getSpecialCells = tool({
  ...toolSchemas.getSpecialCells,
  execute: async ({ address, cellType, cellValueType }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const cellTypeMap: Record<string, Excel.SpecialCellType> = {
        Blanks: Excel.SpecialCellType.blanks,
        Constants: Excel.SpecialCellType.constants,
        Formulas: Excel.SpecialCellType.formulas,
        ConditionalFormats: Excel.SpecialCellType.conditionalFormats,
        DataValidations: Excel.SpecialCellType.dataValidations,
        SameConditionalFormat: Excel.SpecialCellType.sameConditionalFormat,
        SameDataValidation: Excel.SpecialCellType.sameDataValidation,
        Visible: Excel.SpecialCellType.visible,
      }

      const valueTypeMap: Record<string, Excel.SpecialCellValueType> = {
        All: Excel.SpecialCellValueType.all,
        Errors: Excel.SpecialCellValueType.errors,
        ErrorsLogical: Excel.SpecialCellValueType.errorsLogical,
        ErrorsNumbers: Excel.SpecialCellValueType.errorsNumbers,
        ErrorsText: Excel.SpecialCellValueType.errorsText,
        ErrorsLogicalNumber: Excel.SpecialCellValueType.errorsLogicalNumber,
        ErrorsLogicalText: Excel.SpecialCellValueType.errorsLogicalText,
        ErrorsNumberText: Excel.SpecialCellValueType.errorsNumberText,
        Logical: Excel.SpecialCellValueType.logical,
        LogicalNumbers: Excel.SpecialCellValueType.logicalNumbers,
        LogicalText: Excel.SpecialCellValueType.logicalText,
        LogicalNumbersText: Excel.SpecialCellValueType.logicalNumbersText,
        Numbers: Excel.SpecialCellValueType.numbers,
        NumbersText: Excel.SpecialCellValueType.numbersText,
        Text: Excel.SpecialCellValueType.text,
      }

      const addresses: string[] = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)

        const excelCellType = cellTypeMap[cellType]
        const excelValueType = cellValueType
          ? valueTypeMap[cellValueType]
          : undefined

        const rangeAreas = cellValueType
          ? range.getSpecialCells(excelCellType, excelValueType)
          : range.getSpecialCells(excelCellType)

        rangeAreas.load(['address'])
        await context.sync()

        // RangeAreas can contain multiple ranges, get all addresses
        if (rangeAreas.address) {
          // Split by comma if multiple ranges
          const rangeList = rangeAreas.address.split(',').map((a) => a.trim())
          addresses.push(...rangeList)
        }
      })

      return {
        success: true,
        address,
        cellType,
        cellValueType: cellValueType || undefined,
        foundRanges: addresses,
        count: addresses.length,
      }
    } catch (err) {
      return {
        error: `Failed to get special cells: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Adds a comment to a cell (Excel only)
 */
export const addComment = tool({
  ...toolSchemas.addComment,
  execute: async ({ address, content }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        // Comments are added via worksheet.comments.add()
        // The address should include sheet name if not on active sheet
        const { sheetName, rangeAddress } = parseAddressWithSheet(address)

        // Ensure it's a single cell (not a range)
        if (rangeAddress.includes(':')) {
          throw new Error('Comment can only be added to a single cell')
        }

        // Get the worksheet (same pattern as getComments)
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()

        // Add comment using worksheet.comments.add() with just the range address
        sheet.comments.add(rangeAddress, content)
        await context.sync()
      })

      return {
        success: true,
        address,
        content,
      }
    } catch (err) {
      return {
        error: `Failed to add comment: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Deletes an Excel table
 */
export const deleteTable = tool({
  ...toolSchemas.deleteTable,
  execute: async ({ tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        table.delete()
        await context.sync()
      })

      return {
        success: true,
        tableName,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete table: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Adds a column to an Excel table
 */
export const addTableColumn = tool({
  ...toolSchemas.addTableColumn,
  execute: async ({ tableName, columnName, index, values }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let addedColumnName: string = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        const columns = table.columns

        // Add column with optional name and index
        const newColumn = columns.add(index, values, columnName)
        newColumn.load(['name', 'index'])
        await context.sync()

        addedColumnName = newColumn.name
      })

      return {
        success: true,
        tableName,
        columnName: addedColumnName,
        index: index ?? undefined,
      }
    } catch (err) {
      return {
        error: `Failed to add table column: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Deletes a column from an Excel table
 */
export const deleteTableColumn = tool({
  ...toolSchemas.deleteTableColumn,
  execute: async ({ tableName, columnName, columnIndex }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      if (!columnName && columnIndex === undefined) {
        return {
          error: 'Either columnName or columnIndex must be provided',
        }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        const columns = table.columns

        let column: Excel.TableColumn
        if (columnName) {
          column = columns.getItem(columnName)
        } else if (columnIndex !== undefined) {
          column = columns.getItemAt(columnIndex)
        } else {
          throw new Error('Either columnName or columnIndex must be provided')
        }

        column.delete()
        await context.sync()
      })

      return {
        success: true,
        tableName,
        columnName: columnName || `Column ${columnIndex}`,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete table column: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Table operations
export const resizeTable = tool({
  ...toolSchemas.resizeTable,
  execute: async ({ tableName, newRange }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        const { range } = getSheetAndRange(context, newRange)
        table.resize(range)
        await context.sync()
      })

      return {
        success: true,
        tableName,
        newRange,
      }
    } catch (err) {
      return {
        error: `Failed to resize table: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const convertTableToRange = tool({
  ...toolSchemas.convertTableToRange,
  execute: async ({ tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let rangeAddress: string = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        const range = table.convertToRange()
        range.load(['address'])
        await context.sync()
        rangeAddress = range.address
      })

      return {
        success: true,
        tableName,
        rangeAddress,
      }
    } catch (err) {
      return {
        error: `Failed to convert table to range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const clearTableFilters = tool({
  ...toolSchemas.clearTableFilters,
  execute: async ({ tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        table.clearFilters()
        await context.sync()
      })

      return {
        success: true,
        tableName,
      }
    } catch (err) {
      return {
        error: `Failed to clear table filters: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const reapplyTableFilters = tool({
  ...toolSchemas.reapplyTableFilters,
  execute: async ({ tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        table.reapplyFilters()
        await context.sync()
      })

      return {
        success: true,
        tableName,
      }
    } catch (err) {
      return {
        error: `Failed to reapply table filters: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const listTableColumns = tool({
  ...toolSchemas.listTableColumns,
  execute: async ({ tableName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const columns: Array<{ name: string; index: number }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const table = context.workbook.tables.getItem(tableName)
        const columnsCollection = table.columns
        columnsCollection.load('items')
        await context.sync()

        for (const column of columnsCollection.items) {
          column.load(['name', 'index'])
        }
        await context.sync()

        columns.push(
          ...columnsCollection.items.map((col) => ({
            name: col.name,
            index: col.index,
          }))
        )
      })

      return {
        success: true,
        tableName,
        columns,
        count: columns.length,
      }
    } catch (err) {
      return {
        error: `Failed to list table columns: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Chart operations
export const deleteChart = tool({
  ...toolSchemas.deleteChart,
  execute: async ({ chartName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        // Find chart by name - need to search through worksheets
        const sheets = context.workbook.worksheets
        sheets.load('items')
        await context.sync()

        for (const sheet of sheets.items) {
          const charts = sheet.charts
          charts.load('items')
          await context.sync()

          for (const chart of charts.items) {
            chart.load('name')
            await context.sync()
            if (chart.name === chartName) {
              chart.delete()
              await context.sync()
              return
            }
          }
        }

        throw new Error(`Chart "${chartName}" not found`)
      })

      return {
        success: true,
        chartName,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete chart: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const updateChartData = tool({
  ...toolSchemas.updateChartData,
  execute: async ({ chartName, dataRange, hasHeaders, seriesBy }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const seriesByMap: Record<string, Excel.ChartSeriesBy> = {
        Auto: Excel.ChartSeriesBy.auto,
        Columns: Excel.ChartSeriesBy.columns,
        Rows: Excel.ChartSeriesBy.rows,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheets = context.workbook.worksheets
        sheets.load('items')
        await context.sync()

        for (const sheet of sheets.items) {
          const charts = sheet.charts
          charts.load('items')
          await context.sync()

          for (const chart of charts.items) {
            chart.load('name')
            await context.sync()
            if (chart.name === chartName) {
              const { range } = getSheetAndRange(context, dataRange)
              chart.setData(range, seriesByMap[seriesBy || 'Auto'])
              await context.sync()
              return
            }
          }
        }

        throw new Error(`Chart "${chartName}" not found`)
      })

      return {
        success: true,
        chartName,
        dataRange,
      }
    } catch (err) {
      return {
        error: `Failed to update chart data: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const setChartPosition = tool({
  ...toolSchemas.setChartPosition,
  execute: async ({ chartName, startCell, endCell }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheets = context.workbook.worksheets
        sheets.load('items')
        await context.sync()

        for (const sheet of sheets.items) {
          const charts = sheet.charts
          charts.load('items')
          await context.sync()

          for (const chart of charts.items) {
            chart.load('name')
            await context.sync()
            if (chart.name === chartName) {
              // Extract just the cell address (without sheet name) to use with the chart's sheet
              const { rangeAddress: startAddress } =
                parseAddressWithSheet(startCell)
              const startRange = sheet.getRange(startAddress)

              if (endCell) {
                const { rangeAddress: endAddress } =
                  parseAddressWithSheet(endCell)
                const endRange = sheet.getRange(endAddress)
                chart.setPosition(startRange, endRange)
              } else {
                chart.setPosition(startRange)
              }
              await context.sync()
              return
            }
          }
        }

        throw new Error(`Chart "${chartName}" not found`)
      })

      return {
        success: true,
        chartName,
        startCell,
        endCell: endCell || undefined,
      }
    } catch (err) {
      return {
        error: `Failed to set chart position: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const listCharts = tool({
  ...toolSchemas.listCharts,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const charts: Array<{ name: string; chartType: string }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()

        const chartsCollection = sheet.charts
        chartsCollection.load('items')
        await context.sync()

        for (const chart of chartsCollection.items) {
          chart.load(['name', 'chartType'])
        }
        await context.sync()

        charts.push(
          ...chartsCollection.items.map((ch) => ({
            name: ch.name,
            chartType: ch.chartType.toString(),
          }))
        )
      })

      return {
        success: true,
        sheetName: sheetName || 'Active Sheet',
        charts,
        count: charts.length,
      }
    } catch (err) {
      return {
        error: `Failed to list charts: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Range operations
export const moveRange = tool({
  ...toolSchemas.moveRange,
  execute: async ({ sourceAddress, targetAddress }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range: sourceRange } = getSheetAndRange(context, sourceAddress)
        const { range: targetRange } = getSheetAndRange(context, targetAddress)
        sourceRange.moveTo(targetRange)
        await context.sync()
      })

      return {
        success: true,
        sourceAddress,
        targetAddress,
      }
    } catch (err) {
      return {
        error: `Failed to move range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const selectRange = tool({
  ...toolSchemas.selectRange,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.select()
        await context.sync()
      })

      return {
        success: true,
        address,
      }
    } catch (err) {
      return {
        error: `Failed to select range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const groupRange = tool({
  ...toolSchemas.groupRange,
  execute: async ({ address, groupBy }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const groupOptionMap: Record<string, Excel.GroupOption> = {
        ByRows: Excel.GroupOption.byRows,
        ByColumns: Excel.GroupOption.byColumns,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.group(groupOptionMap[groupBy])
        await context.sync()
      })

      return {
        success: true,
        address,
        groupBy,
      }
    } catch (err) {
      return {
        error: `Failed to group range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const ungroupRange = tool({
  ...toolSchemas.ungroupRange,
  execute: async ({ address, groupBy }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const groupOptionMap: Record<string, Excel.GroupOption> = {
        ByRows: Excel.GroupOption.byRows,
        ByColumns: Excel.GroupOption.byColumns,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.ungroup(groupOptionMap[groupBy])
        await context.sync()
      })

      return {
        success: true,
        address,
        groupBy,
      }
    } catch (err) {
      return {
        error: `Failed to ungroup range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getUsedRange = tool({
  ...toolSchemas.getUsedRange,
  execute: async ({ address, valuesOnly }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let usedRangeInfo: {
        address: string
        rowCount: number
        columnCount: number
      } = {
        address: '',
        rowCount: 0,
        columnCount: 0,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        let usedRange: Excel.Range

        if (address) {
          const { range } = getSheetAndRange(context, address)
          usedRange = range.getUsedRange(valuesOnly ?? false)
        } else {
          const sheet = context.workbook.worksheets.getActiveWorksheet()
          usedRange = sheet.getUsedRange(valuesOnly ?? false)
        }

        usedRange.load(['address', 'rowCount', 'columnCount'])
        await context.sync()

        usedRangeInfo = {
          address: usedRange.address,
          rowCount: usedRange.rowCount,
          columnCount: usedRange.columnCount,
        }
      })

      return {
        success: true,
        ...usedRangeInfo,
      }
    } catch (err) {
      return {
        error: `Failed to get used range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Advanced formatting
export const setBorders = tool({
  ...toolSchemas.setBorders,
  execute: async ({ addresses, borders }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const styleMap: Record<string, Excel.BorderLineStyle> = {
        None: Excel.BorderLineStyle.none,
        Continuous: Excel.BorderLineStyle.continuous,
        Dash: Excel.BorderLineStyle.dash,
        DashDot: Excel.BorderLineStyle.dashDot,
        DashDotDot: Excel.BorderLineStyle.dashDotDot,
        Dot: Excel.BorderLineStyle.dot,
        Double: Excel.BorderLineStyle.double,
        SlantDashDot: Excel.BorderLineStyle.slantDashDot,
      }

      const weightMap: Record<string, Excel.BorderWeight> = {
        Hairline: Excel.BorderWeight.hairline,
        Thin: Excel.BorderWeight.thin,
        Medium: Excel.BorderWeight.medium,
        Thick: Excel.BorderWeight.thick,
      }

      const borderStyle = styleMap[borders.style || 'Continuous']
      const borderWeight = weightMap[borders.weight || 'Thin']
      const borderColor = borders.color || '#000000'

      await Excel.run(async (context: Excel.RequestContext) => {
        for (const address of addresses) {
          const { range } = getSheetAndRange(context, address)
          const rangeBorders = range.format.borders

          if (borders.top) {
            rangeBorders.getItem(Excel.BorderIndex.edgeTop).style = borderStyle
            rangeBorders.getItem(Excel.BorderIndex.edgeTop).color = borderColor
            rangeBorders.getItem(Excel.BorderIndex.edgeTop).weight =
              borderWeight
          }
          if (borders.bottom) {
            rangeBorders.getItem(Excel.BorderIndex.edgeBottom).style =
              borderStyle
            rangeBorders.getItem(Excel.BorderIndex.edgeBottom).color =
              borderColor
            rangeBorders.getItem(Excel.BorderIndex.edgeBottom).weight =
              borderWeight
          }
          if (borders.left) {
            rangeBorders.getItem(Excel.BorderIndex.edgeLeft).style = borderStyle
            rangeBorders.getItem(Excel.BorderIndex.edgeLeft).color = borderColor
            rangeBorders.getItem(Excel.BorderIndex.edgeLeft).weight =
              borderWeight
          }
          if (borders.right) {
            rangeBorders.getItem(Excel.BorderIndex.edgeRight).style =
              borderStyle
            rangeBorders.getItem(Excel.BorderIndex.edgeRight).color =
              borderColor
            rangeBorders.getItem(Excel.BorderIndex.edgeRight).weight =
              borderWeight
          }
          if (borders.insideVertical) {
            rangeBorders.getItem(Excel.BorderIndex.insideVertical).style =
              borderStyle
            rangeBorders.getItem(Excel.BorderIndex.insideVertical).color =
              borderColor
            rangeBorders.getItem(Excel.BorderIndex.insideVertical).weight =
              borderWeight
          }
          if (borders.insideHorizontal) {
            rangeBorders.getItem(Excel.BorderIndex.insideHorizontal).style =
              borderStyle
            rangeBorders.getItem(Excel.BorderIndex.insideHorizontal).color =
              borderColor
            rangeBorders.getItem(Excel.BorderIndex.insideHorizontal).weight =
              borderWeight
          }
        }

        await context.sync()
      })

      return {
        success: true,
        addresses,
      }
    } catch (err) {
      return {
        error: `Failed to set borders: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const setColumnWidth = tool({
  ...toolSchemas.setColumnWidth,
  execute: async ({ address, width }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.format.columnWidth = width
        await context.sync()
      })

      return {
        success: true,
        address,
        width,
      }
    } catch (err) {
      return {
        error: `Failed to set column width: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const setRowHeight = tool({
  ...toolSchemas.setRowHeight,
  execute: async ({ address, height }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.format.rowHeight = height
        await context.sync()
      })

      return {
        success: true,
        address,
        height,
      }
    } catch (err) {
      return {
        error: `Failed to set row height: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const autofitColumns = tool({
  ...toolSchemas.autofitColumns,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.format.autofitColumns()
        await context.sync()
      })

      return {
        success: true,
        address,
      }
    } catch (err) {
      return {
        error: `Failed to autofit columns: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const autofitRows = tool({
  ...toolSchemas.autofitRows,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.format.autofitRows()
        await context.sync()
      })

      return {
        success: true,
        address,
      }
    } catch (err) {
      return {
        error: `Failed to autofit rows: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Comment operations
export const updateComment = tool({
  ...toolSchemas.updateComment,
  execute: async ({ address, content }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheetName, rangeAddress } = parseAddressWithSheet(address)
        const fullAddress = sheetName
          ? `${sheetName}!${rangeAddress}`
          : rangeAddress

        // Find comment by cell address
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()

        const comments = sheet.comments
        comments.load('items')
        await context.sync()

        for (const comment of comments.items) {
          const location = comment.getLocation()
          location.load(['address'])
          comment.load(['content'])
          await context.sync()

          if (
            location.address === rangeAddress ||
            location.address === fullAddress
          ) {
            comment.content = content
            await context.sync()
            return
          }
        }

        throw new Error(`No comment found at ${address}`)
      })

      return {
        success: true,
        address,
        content,
      }
    } catch (err) {
      return {
        error: `Failed to update comment: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const deleteComment = tool({
  ...toolSchemas.deleteComment,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheetName, rangeAddress } = parseAddressWithSheet(address)
        const fullAddress = sheetName
          ? `${sheetName}!${rangeAddress}`
          : rangeAddress

        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()

        const comments = sheet.comments
        comments.load('items')
        await context.sync()

        for (const comment of comments.items) {
          const location = comment.getLocation()
          location.load(['address'])
          await context.sync()

          if (
            location.address === rangeAddress ||
            location.address === fullAddress
          ) {
            comment.delete()
            await context.sync()
            return
          }
        }

        throw new Error(`No comment found at ${address}`)
      })

      return {
        success: true,
        address,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete comment: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Workbook operations
export const saveWorkbook = tool({
  ...toolSchemas.saveWorkbook,
  execute: async ({ saveBehavior }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const behaviorMap: Record<string, Excel.SaveBehavior> = {
          Save: Excel.SaveBehavior.save,
          Prompt: Excel.SaveBehavior.prompt,
        }

        context.workbook.save(behaviorMap[saveBehavior || 'Save'])
        await context.sync()
      })

      return {
        success: true,
        saveBehavior: saveBehavior || 'Save',
      }
    } catch (err) {
      return {
        error: `Failed to save workbook: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getActiveCell = tool({
  ...toolSchemas.getActiveCell,
  execute: async ({ includeValue }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let cellInfo: {
        address: string
        value?: unknown
      } = {
        address: '',
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const activeCell = context.workbook.getActiveCell()
        activeCell.load(['address'])

        if (includeValue) {
          activeCell.load(['values'])
        }

        await context.sync()

        cellInfo = {
          address: activeCell.address,
        }

        if (includeValue && activeCell.values) {
          cellInfo.value = activeCell.values[0]?.[0]
        }
      })

      return {
        success: true,
        ...cellInfo,
      }
    } catch (err) {
      return {
        error: `Failed to get active cell: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Data validation
export const getDataValidation = tool({
  ...toolSchemas.getDataValidation,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let validationInfo: {
        rule: unknown
        prompt?: unknown
        errorAlert?: unknown
      } = {
        rule: null,
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        const validation = range.dataValidation
        validation.load(['rule', 'prompt', 'errorAlert'])
        await context.sync()

        validationInfo = {
          rule: validation.rule,
          prompt: validation.prompt,
          errorAlert: validation.errorAlert,
        }
      })

      return {
        success: true,
        address,
        ...validationInfo,
      }
    } catch (err) {
      return {
        error: `Failed to get data validation: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const clearDataValidation = tool({
  ...toolSchemas.clearDataValidation,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        const validation = range.dataValidation
        validation.clear()
        await context.sync()
      })

      return {
        success: true,
        address,
      }
    } catch (err) {
      return {
        error: `Failed to clear data validation: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// Worksheet operations
export const copyWorksheet = tool({
  ...toolSchemas.copyWorksheet,
  execute: async ({ sheetName, newSheetName, position }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let copiedSheetName: string = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const sourceSheet = context.workbook.worksheets.getItem(sheetName)
        const positionMap: Record<string, Excel.WorksheetPositionType> = {
          Before: Excel.WorksheetPositionType.before,
          After: Excel.WorksheetPositionType.after,
          Beginning: Excel.WorksheetPositionType.beginning,
          End: Excel.WorksheetPositionType.end,
        }

        const newSheet = position
          ? sourceSheet.copy(positionMap[position], sourceSheet)
          : sourceSheet.copy()

        if (newSheetName) {
          newSheet.name = newSheetName
        }

        newSheet.load('name')
        await context.sync()
        copiedSheetName = newSheet.name
      })

      return {
        success: true,
        sourceSheet: sheetName,
        newSheetName: copiedSheetName,
      }
    } catch (err) {
      return {
        error: `Failed to copy worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const hideWorksheet = tool({
  ...toolSchemas.hideWorksheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getItem(sheetName)
        sheet.visibility = Excel.SheetVisibility.hidden
        await context.sync()
      })

      return {
        success: true,
        sheetName,
        hidden: true,
      }
    } catch (err) {
      return {
        error: `Failed to hide worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const showWorksheet = tool({
  ...toolSchemas.showWorksheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = context.workbook.worksheets.getItem(sheetName)
        sheet.visibility = Excel.SheetVisibility.visible
        await context.sync()
      })

      return {
        success: true,
        sheetName,
        visible: true,
      }
    } catch (err) {
      return {
        error: `Failed to show worksheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Query a database and write results directly to the spreadsheet.
 * More efficient than queryDatabase + writeCellRanges for large result sets.
 */
export const queryDatabaseToSheet = tool({
  ...toolSchemas.queryDatabaseToSheet,
  execute: async ({
    connectorId,
    query,
    targetAddress,
    includeHeaders,
    autofit,
  }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      // Query the database
      const response = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ action: 'query', params: { query } }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      const rows = result.rows as Array<Record<string, unknown>>

      if (!rows || rows.length === 0) {
        return {
          success: true,
          message: 'Query returned no rows',
          rowCount: 0,
        }
      }

      // Get column names from the first row's keys (most reliable)
      const columnNames = Object.keys(rows[0])
      const values: Array<Array<string | number | null>> = []

      if (includeHeaders ?? true) {
        values.push(columnNames)
      }

      for (const row of rows) {
        const rowValues = columnNames.map((col) => {
          const val = row[col]
          if (val === null || val === undefined) return null
          if (typeof val === 'string' || typeof val === 'number') return val
          return String(val)
        })
        values.push(rowValues)
      }

      // Write to Excel
      let writtenAddress = ''
      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet } = getSheetAndRange(context, targetAddress)
        const { rangeAddress } = parseAddressWithSheet(targetAddress)

        // Resize from single cell to fit values
        const startRange = sheet.getRange(rangeAddress)
        const dataRange = startRange.getResizedRange(
          values.length - 1,
          values[0]?.length ? values[0].length - 1 : 0
        )
        dataRange.values = values

        dataRange.load(['address'])
        await context.sync()

        if (autofit ?? true) {
          dataRange.format.autofitColumns()
          dataRange.format.autofitRows()
          await context.sync()
        }

        writtenAddress = dataRange.address
      })

      return {
        success: true,
        targetAddress: writtenAddress,
        rowCount: rows.length,
        columnCount: columnNames.length,
        includesHeaders: includeHeaders ?? true,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to query database to sheet: ${error}` }
    }
  },
})

// ============================================================================
// Ported from Sheets: Named Ranges, Conditional Formatting, Filters
// ============================================================================

export const createNamedRange = tool({
  ...toolSchemas.createNamedRangeExcel,
  execute: async ({ name, address, scope }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        if (scope === 'Worksheet') {
          const { sheet, range } = getSheetAndRange(context, address)
          sheet.names.add(name, range)
        } else {
          const { range } = getSheetAndRange(context, address)
          context.workbook.names.add(name, range)
        }
        await context.sync()
      })

      return {
        success: true,
        name,
        address,
        scope: scope ?? 'Workbook',
      }
    } catch (err) {
      return {
        error: `Failed to create named range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getNamedRanges = tool({
  ...toolSchemas.getNamedRangesExcel,
  execute: async () => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const namedRanges: Array<{
        name: string
        address: string
        scope: string
      }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const names = context.workbook.names
        names.load('items')
        await context.sync()

        for (const item of names.items) {
          item.load(['name', 'type', 'value'])
          await context.sync()

          namedRanges.push({
            name: item.name,
            address: item.value,
            scope: 'Workbook',
          })
        }
      })

      return {
        success: true,
        namedRanges,
        count: namedRanges.length,
      }
    } catch (err) {
      return {
        error: `Failed to get named ranges: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const deleteNamedRange = tool({
  ...toolSchemas.deleteNamedRangeExcel,
  execute: async ({ name }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const namedItem = context.workbook.names.getItem(name)
        namedItem.delete()
        await context.sync()
      })

      return {
        success: true,
        name,
        deleted: true,
      }
    } catch (err) {
      return {
        error: `Failed to delete named range: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const setFilter = tool({
  ...toolSchemas.setFilterExcel,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { sheet, range } = getSheetAndRange(context, address)
        // Remove existing filter if any
        const existingFilter = sheet.autoFilter
        existingFilter.load('enabled')
        await context.sync()
        if (existingFilter.enabled) {
          existingFilter.remove()
          await context.sync()
        }
        // Apply new auto-filter via Worksheet.autoFilter.apply()
        sheet.autoFilter.apply(range)
        await context.sync()
      })

      return {
        success: true,
        address,
        filtered: true,
      }
    } catch (err) {
      return {
        error: `Failed to set filter: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const removeFilter = tool({
  ...toolSchemas.removeFilterExcel,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()
        const autoFilter = sheet.autoFilter
        autoFilter.load('enabled')
        await context.sync()
        if (autoFilter.enabled) {
          autoFilter.remove()
          await context.sync()
        }
      })

      return {
        success: true,
        removed: true,
      }
    } catch (err) {
      return {
        error: `Failed to remove filter: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const addConditionalFormatRule = tool({
  ...toolSchemas.addConditionalFormatRuleExcel,
  execute: async ({ address, rule }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)

        if (rule.ruleType === 'cellValue') {
          const cf = range.conditionalFormats.add(
            Excel.ConditionalFormatType.cellValue
          )
          const operatorMap: Record<
            string,
            Excel.ConditionalCellValueOperator
          > = {
            EqualTo: Excel.ConditionalCellValueOperator.equalTo,
            NotEqualTo: Excel.ConditionalCellValueOperator.notEqualTo,
            GreaterThan: Excel.ConditionalCellValueOperator.greaterThan,
            GreaterThanOrEqual:
              Excel.ConditionalCellValueOperator.greaterThanOrEqual,
            LessThan: Excel.ConditionalCellValueOperator.lessThan,
            LessThanOrEqual: Excel.ConditionalCellValueOperator.lessThanOrEqual,
            Between: Excel.ConditionalCellValueOperator.between,
            NotBetween: Excel.ConditionalCellValueOperator.notBetween,
          }

          cf.cellValue.rule = {
            operator: operatorMap[rule.operator],
            formula1: rule.formula1,
            ...(rule.formula2 ? { formula2: rule.formula2 } : {}),
          }

          if (rule.format.backgroundColor) {
            cf.cellValue.format.fill.color = rule.format.backgroundColor
          }
          if (rule.format.fontColor) {
            cf.cellValue.format.font.color = rule.format.fontColor
          }
          if (rule.format.bold !== undefined) {
            cf.cellValue.format.font.bold = rule.format.bold
          }
          if (rule.format.italic !== undefined) {
            cf.cellValue.format.font.italic = rule.format.italic
          }
        } else if (rule.ruleType === 'colorScale') {
          const cf = range.conditionalFormats.add(
            Excel.ConditionalFormatType.colorScale
          )
          cf.colorScale.criteria = {
            minimum: { color: rule.minColor },
            ...(rule.midColor ? { midpoint: { color: rule.midColor } } : {}),
            maximum: { color: rule.maxColor },
          }
        } else if (rule.ruleType === 'customFormula') {
          const cf = range.conditionalFormats.add(
            Excel.ConditionalFormatType.custom
          )
          cf.custom.rule.formula = rule.formula
          if (rule.format.backgroundColor) {
            cf.custom.format.fill.color = rule.format.backgroundColor
          }
          if (rule.format.fontColor) {
            cf.custom.format.font.color = rule.format.fontColor
          }
          if (rule.format.bold !== undefined) {
            cf.custom.format.font.bold = rule.format.bold
          }
          if (rule.format.italic !== undefined) {
            cf.custom.format.font.italic = rule.format.italic
          }
        }

        await context.sync()
      })

      return {
        success: true,
        address,
        ruleType: rule.ruleType,
      }
    } catch (err) {
      return {
        error: `Failed to add conditional format rule: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const clearConditionalFormatRules = tool({
  ...toolSchemas.clearConditionalFormatRulesExcel,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        range.conditionalFormats.clearAll()
        await context.sync()
      })

      return {
        success: true,
        address,
        cleared: true,
      }
    } catch (err) {
      return {
        error: `Failed to clear conditional format rules: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getConditionalFormatRules = tool({
  ...toolSchemas.getConditionalFormatRulesExcel,
  execute: async ({ address }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      const rules: Array<{ type: string; priority: number }> = []

      await Excel.run(async (context: Excel.RequestContext) => {
        const { range } = getSheetAndRange(context, address)
        const cfs = range.conditionalFormats
        cfs.load('items')
        await context.sync()

        for (const cf of cfs.items) {
          cf.load(['type', 'priority'])
          await context.sync()
          rules.push({
            type: cf.type,
            priority: cf.priority,
          })
        }
      })

      return {
        success: true,
        address,
        rules,
        count: rules.length,
      }
    } catch (err) {
      return {
        error: `Failed to get conditional format rules: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const protectSheet = tool({
  ...toolSchemas.protectSheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let protectedSheetName = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()
        sheet.load('name')
        sheet.protection.protect()
        await context.sync()
        protectedSheetName = sheet.name
      })

      return {
        success: true,
        sheetName: protectedSheetName,
        protected: true,
      }
    } catch (err) {
      return {
        error: `Failed to protect sheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const unprotectSheet = tool({
  ...toolSchemas.unprotectSheet,
  execute: async ({ sheetName }) => {
    try {
      const Excel = window.Excel
      if (!Excel) {
        return { error: 'Excel runtime not available' }
      }

      let unprotectedSheetName = ''

      await Excel.run(async (context: Excel.RequestContext) => {
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()
        sheet.load('name')
        sheet.protection.unprotect()
        await context.sync()
        unprotectedSheetName = sheet.name
      })

      return {
        success: true,
        sheetName: unprotectedSheetName,
        unprotected: true,
      }
    } catch (err) {
      return {
        error: `Failed to unprotect sheet: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const excelTools = {
  readCellRanges,
  writeCellRanges,
  setFormulaRanges,
  readSheetContext,
  getCellFormat,
  formatCells,
  getSheetDimensions,
  getSelectedRange,
  executeCode,
  clearCellRange,
  insertRows,
  insertColumns,
  deleteRows,
  deleteColumns,
  createTable,
  sortRange,
  findAndReplace,
  mergeCells,
  unmergeCells,
  addHyperlink,
  listWorksheets,
  switchWorksheet,
  createWorksheet,
  copyRange,
  createChart,
  fetchDataFromUrl,
  queryDatabase,
  queryDatabaseToSheet,
  setDataValidation,
  getComments,
  freezePanes,
  getTableRows,
  addTableRow,
  pasteAttachedCsvToSheet,
  autoFill,
  removeDuplicates,
  deleteWorksheet,
  renameWorksheet,
  getSpecialCells,
  addComment,
  deleteTable,
  addTableColumn,
  deleteTableColumn,
  // Table operations
  resizeTable,
  convertTableToRange,
  clearTableFilters,
  reapplyTableFilters,
  listTableColumns,
  // Chart operations
  deleteChart,
  updateChartData,
  setChartPosition,
  listCharts,
  // Range operations
  moveRange,
  selectRange,
  groupRange,
  ungroupRange,
  getUsedRange,
  // Advanced formatting
  setBorders,
  setColumnWidth,
  setRowHeight,
  autofitColumns,
  autofitRows,
  // Comment operations
  updateComment,
  deleteComment,
  // Workbook operations
  saveWorkbook,
  getActiveCell,
  // Data validation
  getDataValidation,
  clearDataValidation,
  // Worksheet operations
  copyWorksheet,
  hideWorksheet,
  showWorksheet,
  // Ported from Sheets
  createNamedRange,
  getNamedRanges,
  deleteNamedRange,
  setFilter,
  removeFilter,
  addConditionalFormatRule,
  clearConditionalFormatRules,
  getConditionalFormatRules,
  // Sheet protection
  protectSheet,
  unprotectSheet,
}
